import { Switch } from '@base-ui/react/switch';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

export interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked = false,
      onChange,
      disabled = false,
      size = 'md',
      className,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'w-[32px] h-[16px]',
      md: 'w-[40px] h-[20px]'
    };

    const handleSizeClasses = {
      sm: 'w-[12px] h-[12px]',
      md: 'w-[16px] h-[16px]'
    };

    const bgColor = disabled ? 'bg-gray-300' : checked ? 'bg-secondary-blue' : 'bg-content-subtlest';

    return (
      <Switch.Root
        id={id}
        checked={checked}
        disabled={disabled}
        nativeButton
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className={twMerge(
          'relative rounded-full transition-colors duration-200 ease-in-out p-0 border-0',
          sizeClasses[size],
          bgColor,
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        onCheckedChange={(next) => onChange?.(next)}
        render={<button type="button" ref={ref} />}
      >
        <Switch.Thumb
          className={twMerge(
            'absolute top-1/2 -translate-y-1/2 left-[2px] bg-white rounded-full transition-transform duration-200 ease-in-out block',
            handleSizeClasses[size],
            size === 'sm' ? 'data-[checked]:translate-x-[16px]' : 'data-[checked]:translate-x-[20px]'
          )}
        />
      </Switch.Root>
    );
  }
);

Toggle.displayName = 'Toggle';

export { Toggle };
